/* eslint-disable no-param-reassign */
import PropTypes from "prop-types";
import React, { useState, useCallback, useRef } from "react";
import { Container, Header, Button, Modal } from "semantic-ui-react";
import { Formik, Field } from "formik";
import { useDispatch } from "react-redux";
import { Input, Form, Checkbox } from "formik-semantic-ui-react";
import { useIntl } from "react-intl";
import ConfirmationDialog from "shared/components/dialogs/ConfirmationDialog";
import SemanticFormikImageSelector from "shared/components/forms/SemanticFormikImageSelector";
import Growl from "../../../actions/growlActions";
import { ExternalSalesPartnerResource } from "../../../actions/externalSalesPartnerActions";

const EMPTY_PARTNER = {
  name: "",
  name_full: "",
  email: "",
  phone: "",
  address_street: "",
  address_postcode: "",
  address_city: "",
  marketed_by_agent: false,
  logo_id: null
};

const ExternalSalesPartnerDialog = ({
  model,
  trigger,
  resource,
  projectId
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const refResetForm = useRef();
  const [open, setOpen] = useState(false);

  const clearForm = () => {
    if (typeof refResetForm.current === "function") refResetForm.current();
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  const onSubmit = useCallback(
    values => {
      if (model?.id) {
        values.id = model.id;
      }

      return resource
        .save(values)
        .then(() => {
          new Growl(dispatch).success(
            "message.success.title",
            "message.success.body"
          );
          return resource.fetchAll().then(handleClose);
        })
        .catch(() => {
          new Growl(dispatch).error(
            "message.error.title",
            "message.error.body"
          );
        });
    },
    [model]
  );

  const onDelete = () => {
    resource
      .remove(model?.id)
      .then(() => {
        new Growl(dispatch).success(
          "message.success.title",
          "message.success.body"
        );
        return resource.fetchAll().then(handleClose);
      })
      .catch(() => {
        new Growl(dispatch).error("message.error.title", "message.error.body");
      });
  };

  const renderDeleteButton = isSubmitting => {
    const button = (
      <Button
        id="delete"
        color="red"
        basic
        disabled={isSubmitting}
        loading={isSubmitting}
        content={intl.formatMessage({ id: "meta.actions.remove" })}
        className="left floated element"
      />
    );

    const buttons = [
      {
        id: "delete",
        label: "meta.actions.remove",
        color: "red",
        onClick: onDelete
      },
      {
        id: "cancel",
        label: "meta.actions.cancel",
        basic: true
      }
    ];

    return (
      <ConfirmationDialog
        title="project.attributes.external_sales.dialog.deleteTitle"
        message="project.attributes.external_sales.dialog.deleteMessage"
        buttons={buttons}
        button={button}
      />
    );
  };

  return (
    <Formik
      initialValues={model ?? EMPTY_PARTNER}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit, isSubmitting, resetForm }) => {
        refResetForm.current = resetForm;
        return (
          <Modal
            trigger={trigger}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            size="small"
          >
            <Header
              content={intl.formatMessage({
                id: model?.id
                  ? "project.attributes.external_sales.dialog.editTitle"
                  : "project.attributes.external_sales.dialog.newTitle"
              })}
            />
            <Modal.Content scrolling>
              <Container>
                <Form>
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id: "project.attributes.external_sales.agent_name.label"
                    })}
                    name="name"
                    errorPrompt
                  />
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.agent_name_full.label"
                    })}
                    name="name_full"
                    errorPrompt
                  />
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id: "project.attributes.external_sales.agent_email.label"
                    })}
                    name="email"
                    errorPrompt
                  />
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id: "project.attributes.external_sales.agent_phone.label"
                    })}
                    name="phone"
                    errorPrompt
                  />
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.agent_address_street.label"
                    })}
                    name="address_street"
                    errorPrompt
                  />
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.agent_address_postcode.label"
                    })}
                    name="address_postcode"
                    errorPrompt
                  />
                  <Input
                    fluid
                    label={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.agent_address_city.label"
                    })}
                    name="address_city"
                    errorPrompt
                  />
                  <Checkbox
                    name="marketed_by_agent"
                    label={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.marketed_by_agent.label"
                    })}
                  />
                  <Field
                    name="logo_id"
                    label={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.agent_logo_id.label"
                    })}
                    component={SemanticFormikImageSelector}
                    title={intl.formatMessage({
                      id:
                        "project.attributes.external_sales.agent_logo_id.label"
                    })}
                    projectId={projectId}
                    multiple={false}
                  />
                </Form>
              </Container>
            </Modal.Content>
            <Modal.Actions>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div>{model?.id && renderDeleteButton(isSubmitting)}</div>
                <div>
                  <Button
                    content={intl.formatMessage({ id: "meta.actions.cancel" })}
                    onClick={handleClose}
                  />
                  <Button
                    type="submit"
                    content={intl.formatMessage({ id: "meta.actions.save" })}
                    loading={isSubmitting}
                    positive
                    onClick={handleSubmit}
                  />
                </div>
              </div>
            </Modal.Actions>
          </Modal>
        );
      }}
    </Formik>
  );
};

ExternalSalesPartnerDialog.propTypes = {
  trigger: PropTypes.element.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  model: PropTypes.object,
  resource: PropTypes.instanceOf(ExternalSalesPartnerResource).isRequired,
  projectId: PropTypes.number.isRequired
};

ExternalSalesPartnerDialog.defaultProps = {
  model: null
};

export default ExternalSalesPartnerDialog;
